import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { Building2, MapPin, Phone, Mail, AlertTriangle, ArrowLeft, Wallet, Plus, X } from "lucide-react";
import WhatsAppShortcuts from "@/components/WhatsAppShortcuts";
import WhatsAppButton from "@/components/WhatsAppButton";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line } from "recharts";
import { toast } from "sonner";
import AdminDeleteButton from "@/components/AdminDeleteButton";

export default function VendorDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [payForm, setPayForm] = useState({ amount: 0, method: "bank", reference: "", notes: "" });
  const [showPay, setShowPay] = useState(false);

  const load = () => api.get(`/vendors/${id}/transactions`).then(r => setD(r.data)).catch(e => setErr(e.response?.data?.detail || "Could not load vendor"));
  useEffect(() => { load(); }, [id]);

  if (err) return (
    <div className="p-8">
      <Link to="/vendors" className="btn-ghost text-xs inline-flex items-center gap-1"><ArrowLeft size={12}/>Back to vendors</Link>
      <div className="mt-6 text-[#B83A3A]">{err}</div>
    </div>
  );
  if (!d) return <div className="p-8 text-[#75706A]">Loading…</div>;
  const v = d.vendor;
  const t = d.totals;

  const addPayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount) return toast.error("Enter amount");
    try {
      await api.post(`/vendors/${id}/payments`, payForm);
      toast.success("Payment recorded");
      setPayForm({ amount: 0, method: "bank", reference: "", notes: "" });
      setShowPay(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const reminderMsg = () => {
    return `Hi ${v.name}, this is Marsh Clothing. As per our books:\n\n• Total billed: ₹${Number(t.billed).toLocaleString("en-IN")}\n• Paid so far: ₹${Number(t.paid).toLocaleString("en-IN")}\n• Outstanding: ₹${Number(t.outstanding).toLocaleString("en-IN")}\n\nCould you please share the settlement / payment status at your convenience? Thank you 🙏`;
  };
  const askForBill = () => {
    return `Hi ${v.name}, please share the invoice / bill for the recent pending jobs so we can process payment from our side. Thanks!`;
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/vendors" className="btn-ghost text-xs inline-flex items-center gap-1"><ArrowLeft size={12}/>Vendors</Link>
      </div>

      <div className="card-surface p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="label-tiny mb-1 flex items-center gap-1"><Building2 size={11}/> Vendor</div>
            <h1 className="font-display text-3xl tracking-tight">{v.name}</h1>
            <div className="mt-3 space-y-1 text-sm text-[#75706A]">
              {v.contact && <div>Contact person: <b className="text-[#2C2B29]">{v.contact}</b></div>}
              {v.phone && <div className="flex items-center gap-1.5"><Phone size={11}/> <span className="mono">{v.phone}</span></div>}
              {v.email && <div className="flex items-center gap-1.5"><Mail size={11}/> {v.email}</div>}
              {v.address && <div className="flex items-start gap-1.5"><MapPin size={11} className="mt-0.5"/> {v.address}</div>}
              {v.material_types && <div>Handles: {v.material_types}</div>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {v.phone && <WhatsAppShortcuts phone={v.phone} name={v.name} testid={`wa-vendor-${v.id}`}/>}
            {v.map_link && <a href={v.map_link} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1"><MapPin size={11}/> Open on map</a>}
          </div>
        </div>
      </div>

      {/* Damage / quality summary */}
      {d.totals.pieces_damaged > 0 && (
        <div className="card-surface p-4 border-l-4 border-l-[#B83A3A] bg-[#FBEBE7]" data-testid="damage-highlight">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-[#B83A3A] shrink-0" size={18}/>
            <div>
              <div className="font-display font-bold text-[#7C2626]">Quality flag</div>
              <div className="text-sm text-[#7C2626]">
                {d.totals.pieces_damaged} piece{d.totals.pieces_damaged>1?"s":""} reported damaged across {d.customizations.filter(c=>Number(c.qty_damaged||0)>0).length} job{d.customizations.filter(c=>Number(c.qty_damaged||0)>0).length>1?"s":""}.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Purchase orders" value={d.totals.po_count} />
        <Kpi label="Customization jobs" value={d.totals.job_count} />
        <Kpi label="Material value" value={inr(d.totals.material_value)} />
        <Kpi label="Customization value" value={inr(d.totals.customization_value)} />
      </div>

      {/* Payables ledger */}
      <section className="card-surface p-5" data-testid="vendor-payables">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Wallet size={16} className="text-[#C45A41]"/>
            <h2 className="font-display font-bold text-lg">Payables ledger</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {v.phone && (
              <>
                <WhatsAppButton phone={v.phone} message={reminderMsg()} label="Send settlement reminder" size="sm" testid="wa-vendor-reminder"/>
                <WhatsAppButton phone={v.phone} message={askForBill()} label="Ask for bill" size="sm" testid="wa-vendor-askbill"/>
              </>
            )}
            <button className="btn-primary text-xs flex items-center gap-1" onClick={() => setShowPay(s => !s)} data-testid="vendor-add-payment-btn">
              {showPay ? <><X size={12}/> Cancel</> : <><Plus size={12}/> Record payment</>}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <Kpi label="Total billed" value={inr(t.billed)} tone="text-[#2C2B29]" />
          <Kpi label="Paid so far" value={inr(t.paid)} tone="text-[#4A7258]" />
          <Kpi label="Outstanding" value={inr(t.outstanding)} tone={t.outstanding > 0 ? "text-[#B83A3A]" : "text-[#4A7258]"} testid="vendor-outstanding"/>
        </div>

        {showPay && (
          <form onSubmit={addPayment} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 bg-[#FBF8F1] border border-[#EAE5DB] rounded-md mb-4" data-testid="vendor-pay-form">
            <div>
              <div className="label-tiny mb-1">Amount ₹</div>
              <input type="number" required className="input-field" value={payForm.amount||""} onChange={e=>setPayForm({...payForm, amount: Number(e.target.value)||0})} data-testid="vendor-pay-amount"/>
            </div>
            <div>
              <div className="label-tiny mb-1">Method</div>
              <select className="input-field" value={payForm.method} onChange={e=>setPayForm({...payForm, method: e.target.value})}>
                <option value="bank">Bank / NEFT</option>
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <div className="label-tiny mb-1">Reference / UTR</div>
              <input className="input-field" value={payForm.reference} onChange={e=>setPayForm({...payForm, reference: e.target.value})}/>
            </div>
            <div>
              <div className="label-tiny mb-1">Notes</div>
              <input className="input-field" value={payForm.notes} onChange={e=>setPayForm({...payForm, notes: e.target.value})}/>
            </div>
            <div className="md:col-span-5 flex justify-end">
              <button type="submit" className="btn-primary text-sm" data-testid="vendor-pay-submit">Save payment</button>
            </div>
          </form>
        )}

        {(d.payments || []).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Date</th><th>Amount</th><th>Method</th><th>Reference</th><th>Notes</th><th></th></tr></thead>
              <tbody>
                {d.payments.map(p => (
                  <tr key={p.id} data-testid={`vendor-pay-${p.id}`}>
                    <td className="mono text-xs">{(p.paid_at||"").slice(0,10)}</td>
                    <td className="mono">{inr(p.amount)}</td>
                    <td className="capitalize">{p.method}</td>
                    <td className="mono text-xs">{p.reference || "—"}</td>
                    <td className="text-xs">{p.notes || "—"}</td>
                    <td>
                      <AdminDeleteButton endpoint={`/vendors/${id}/payments/${p.id}`} label={`payment ${inr(p.amount)}`} onDone={load} testid={`vendor-pay-del-${p.id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-xs text-[#75706A]">No payments recorded yet. Every payment you record here reduces the outstanding.</div>
        )}
      </section>

      {/* Damage trend */}
      {(d.damage_trend || []).length > 0 && (
        <section className="card-surface p-5" data-testid="vendor-damage-trend">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-[#B83A3A]"/>
            <h2 className="font-display font-bold text-lg">Damage % by month</h2>
          </div>
          <div style={{width:"100%", height:220}}>
            <ResponsiveContainer>
              <LineChart data={d.damage_trend}>
                <XAxis dataKey="month" tick={{fontSize:11}}/>
                <YAxis tick={{fontSize:11}} unit="%"/>
                <Tooltip formatter={(v, n) => n === "percent" ? `${v}%` : v} />
                <Line type="monotone" dataKey="percent" stroke="#B83A3A" strokeWidth={2} dot={{r:3}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Purchase Orders */}
      <section>
        <h2 className="font-display font-bold text-lg mb-2">Purchase Orders</h2>
        <div className="card-surface overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Order</th><th>Customer</th><th>PO #</th><th>Material</th><th>Qty</th><th>Amount</th><th>Expected</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {d.procurements.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-[#75706A]">No POs raised on this vendor yet.</td></tr>}
              {d.procurements.map(p => {
                const poUrl = `${window.location.origin}/print/po/${p.order_id}?po=${p.id}`;
                const poMsg = `Purchase Order ${p.order_no}${p.po_no ? " · " + p.po_no : ""}\n${p.material}${p.qty ? ` · ${p.qty} pcs` : ""}\n\nOpen: ${poUrl}`;
                return (
                  <tr key={p.id} data-testid={`vendor-po-${p.id}`}>
                    <td><Link to={`/orders/${p.order_id}`} className="mono hover:underline">{p.order_no}</Link></td>
                    <td className="text-xs">{p.customer_name}</td>
                    <td className="mono">{p.po_no || "—"}</td>
                    <td>{p.material}</td>
                    <td className="mono">{p.qty}</td>
                    <td className="mono">{inr(p.amount)}</td>
                    <td className="mono">{p.expected_at || "—"}</td>
                    <td>{p.received_at ? <span className="badge badge-success">Received</span> : <span className="badge badge-warn">Pending</span>}</td>
                    <td>
                      <div className="flex gap-1.5 items-center">
                        <a href={poUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs">Preview</a>
                        {v.phone && <WhatsAppButton phone={v.phone} message={poMsg} label="Send" size="sm"/>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Customization Jobs */}
      <section>
        <h2 className="font-display font-bold text-lg mb-2">Customization Jobs</h2>
        <div className="card-surface overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Order</th><th>Customer</th><th>Type</th><th>Sent</th><th>Rec.</th><th>Damaged</th><th>Cost</th><th>Status</th><th>Job Card</th></tr></thead>
            <tbody>
              {d.customizations.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-[#75706A]">No customization jobs assigned to this vendor.</td></tr>}
              {d.customizations.map(c => {
                const jobUrl = `${window.location.origin}/print/job-card/${c.order_id}?job=${c.id}`;
                const jobMsg = `Job Card ${c.order_no} — ${(c.type||"").toUpperCase()}\n${c.qty_sent || "?"} pcs\n\nOpen: ${jobUrl}`;
                const damaged = Number(c.qty_damaged || 0) > 0;
                return (
                  <tr key={c.id} className={damaged ? "bg-[#FBEBE7]" : ""} data-testid={`vendor-job-${c.id}`}>
                    <td><Link to={`/orders/${c.order_id}`} className="mono hover:underline">{c.order_no}</Link></td>
                    <td className="text-xs">{c.customer_name}</td>
                    <td className="capitalize">{c.type}</td>
                    <td className="mono">{c.qty_sent || "—"}</td>
                    <td className="mono">{c.qty_received || "—"}</td>
                    <td className={`mono ${damaged ? "text-[#B83A3A] font-bold" : ""}`} title={c.damage_notes||""}>{c.qty_damaged || "—"}</td>
                    <td className="mono">{inr(c.cost)}</td>
                    <td>{c.completed_at ? <span className="badge badge-success">Done</span> : <span className="badge badge-warn">In progress</span>}</td>
                    <td>
                      <div className="flex gap-1.5 items-center">
                        <a href={jobUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs">Preview</a>
                        {v.phone && <WhatsAppButton phone={v.phone} message={jobMsg} label="Send" size="sm"/>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const Kpi = ({ label, value, tone, testid }) => (
  <div className="card-surface p-4" data-testid={testid}>
    <div className="label-tiny">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${tone||""}`}>{value}</div>
  </div>
);
